// components/DynamicSiloSelector.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { Select, Spin, Button, Modal, Form, Input, InputNumber, message, Space } from 'antd';
import { PlusOutlined, InfoCircleOutlined } from '@ant-design/icons';
import api from '../api/client';

const { Option } = Select;

const DynamicSiloSelector = ({ 
    value, 
    onChange, 
    typeSlug, 
    farmId, 
    placeholder = "Select silo",
    disabled = false,
    showAddButton = true,
    onSiloCreated
}) => {
    const [silos, setSilos] = useState([]);
    const [loading, setLoading] = useState(false);
    const [modalVisible, setModalVisible] = useState(false);
    const [form] = Form.useForm();
    const [submitting, setSubmitting] = useState(false);
    
    const fetchSilos = useCallback(async () => {
        setLoading(true);
        try {
            const params = {};
            if (typeSlug) params.type_slug = typeSlug;
            if (farmId) params.farm_id = farmId;
            
            const response = await api.get(`/silos`, { params });
            setSilos(response.data);
        } catch (error) {
            console.error('Error fetching silos:', error);
            message.error('Failed to load silos');
        } finally {
            setLoading(false);
        }
    }, [typeSlug, farmId]);
    
    useEffect(() => {
        if (typeSlug) {
            fetchSilos();
        }
    }, [fetchSilos]);
    
    const handleAddSilo = async (values) => {
        setSubmitting(true);
        try {
            const response = await api.post(`/silos`, {
                ...values,
                type_slug: typeSlug,
                farm_id: farmId,
                user_id: 1,
                user_name: 'Admin'
            });
            
            if (response.data.success) {
                message.success('Silo created successfully');
                form.resetFields();
                setModalVisible(false);
                await fetchSilos();
                
                // Auto-select the newly created silo
                if (onSiloCreated) {
                    onSiloCreated(response.data.data);
                }
                if (onChange) {
                    onChange(response.data.data.id);
                }
            }
        } catch (error) {
            console.error('Error creating silo:', error);
            message.error(error.response?.data?.error || 'Failed to create silo');
        } finally {
            setSubmitting(false);
        }
    };
    
    return (
        <div style={{ display: 'flex', gap: 8 }}>
            <Select
                value={value}
                onChange={onChange}
                placeholder={placeholder}
                loading={loading}
                disabled={disabled || !typeSlug}
                style={{ flex: 1 }}
                showSearch
                optionFilterProp="children"
                notFoundContent={loading ? <Spin size="small" /> : 'No silos available'}
                dropdownRender={(menu) => (
                    <>
                        {menu}
                        {showAddButton && typeSlug && (
                            <div style={{ padding: '8px', borderTop: '1px solid #f0f0f0' }}>
                                <Button 
                                    type="link" 
                                    icon={<PlusOutlined />} 
                                    onClick={() => setModalVisible(true)}
                                    style={{ width: '100%', textAlign: 'center' }}
                                >
                                    Add New Silo for {typeSlug.toUpperCase()}
                                </Button>
                            </div>
                        )}
                    </>
                )}
            >
                {silos.map(silo => {
                    return (
                        <Option key={silo.id} value={silo.id}>
                            <Space direction="vertical" size={0} style={{ width: '100%' }}>
                                <div>
                                    {/* <Tag color={getOccupancyStatus(silo)}>
                                        {silo.silo_number}
                                    </Tag> */}
                                    <strong>{silo.name}</strong>
                                </div>
                                {/* <div style={{ fontSize: 11, color: '#666' }}>
                                    {silo.location && `📍 ${silo.location} | `}
                                    Capacity: {silo.capacity || 'Unlimited'} units
                                    {silo.current_occupancy > 0 && ` | Occupied: ${silo.current_occupancy}`}
                                    {occupancyPercent > 0 && (
                                        <Tooltip title={`${occupancyPercent.toFixed(1)}% full`}>
                                            <span style={{ marginLeft: 8 }}>
                                                <InfoCircleOutlined style={{ color: getOccupancyStatus(silo) }} />
                                            </span>
                                        </Tooltip>
                                    )}
                                </div> */}
                            </Space>
                        </Option>
                    );
                })}
            </Select>
            
            {/* Add Silo Modal */}
            <Modal
                title={`Add New ${typeSlug?.toUpperCase() || ''} Silo`}
                open={modalVisible}
                onCancel={() => {
                    setModalVisible(false);
                    form.resetFields();
                }}
                footer={[
                    <Button key="cancel" onClick={() => setModalVisible(false)}>Cancel</Button>,
                    <Button key="submit" type="primary" loading={submitting} onClick={() => form.submit()}>
                        Create Silo
                    </Button>
                ]}
                width={500}
            >
                <Form form={form} layout="vertical" onFinish={handleAddSilo}>
                    <Form.Item name="silo_number" label="Silo Number" rules={[{ required: true }]}>
                        <Input placeholder={`e.g., ${typeSlug?.toUpperCase()}-01`} />
                    </Form.Item>
                    
                    <Form.Item name="name" label="Silo Name">
                        <Input placeholder={`e.g., ${typeSlug?.toUpperCase()} Pen 1`} />
                    </Form.Item>
                    
                    <Form.Item name="capacity" label="Capacity (Units)">
                        <InputNumber min={0} style={{ width: '100%' }} placeholder="Maximum capacity" />
                    </Form.Item>
                    
                    <Form.Item name="location" label="Location">
                        <Input placeholder="e.g., Barn A, Section 2" />
                    </Form.Item>
                    
                    <div style={{ fontSize: 12, color: '#666', marginTop: 8 }}>
                        <InfoCircleOutlined /> This silo will be assigned to {typeSlug?.toUpperCase()} type.
                    </div>
                </Form>
            </Modal>
        </div>
    );
};

export default DynamicSiloSelector;